import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const cardVariants = cva(
  "rounded-lg text-card-foreground transition-[transform,box-shadow,background-color,border-color] duration-normal ease-editorial",
  {
    variants: {
      variant: {
        default: "glass-1",
        elevated: "glass-2 elev-md",
        premium: "glass-3 elev-copper",
        outline: "border border-border/60 bg-background/40 backdrop-blur-sm",
        subtle: "glass-0",
      },
      interactive: {
        true: "lift cursor-pointer focus-ring hover:border-primary/40 active:scale-[0.995]",
        false: "",
      },
      loading: {
        true: "animate-pulse-soft pointer-events-none",
        false: "",
      },
    },
    defaultVariants: {
      variant: "default",
      interactive: false,
      loading: false,
    },
  },
);

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, interactive, loading, tabIndex, ...props }, ref) => (
    <div
      ref={ref}
      tabIndex={interactive ? tabIndex ?? 0 : tabIndex}
      aria-busy={loading || undefined}
      className={cn(cardVariants({ variant, interactive, loading }), className)}
      {...props}
    />
  ),
);
Card.displayName = "Card";

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-col space-y-1.5 p-6", className)} {...props} />
  ),
);
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3
      ref={ref}
      className={cn("font-display text-2xl font-medium leading-none tracking-tight text-balance", className)}
      {...props}
    />
  ),
);
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn("text-sm text-muted-foreground text-pretty", className)} {...props} />
  ),
);
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />,
);
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex items-center p-6 pt-0", className)} {...props} />
  ),
);
CardFooter.displayName = "CardFooter";

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent, cardVariants };
